/**
 * NX actions (server migration 065): two tiers of NX access, chosen by risk.
 *
 * The monitoring login (the credential on the host's asset) is used every
 * minute and should only be able to look. The ELEVATED login chosen here — a
 * Power User — is handed to the agent for one action an admin asks for, with
 * a reason, audited, and forgotten when the action ends. Without one, an
 * action runs on the monitoring login. Every action here is read-only.
 */

import { Play } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { NxResultView } from './NxResults.tsx';
import type { HostLogins } from './nx-logins.ts';
import { Banner, Button, Drawer, Field } from './components/index.ts';

interface CredentialOption {
  id: string;
  label: string;
  kind: string;
}

interface ActionRow {
  id: string;
  reason: string;
  action: string | null;
  why: string | null;
  requestedAt: string;
  receivedAt: string | null;
}

const ACTIONS: Array<{ key: 'diagnose_offline' | 'storage_detail'; label: string; what: string }> = [
  { key: 'diagnose_offline', label: 'Diagnose offline cameras', what: 'NX probes each camera it calls offline and says why (up to 30).' },
  { key: 'storage_detail', label: 'Storage detail', what: 'Each disk: writable, rebuilding, backup, free and reserved space; recording statistics.' },
];

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

export function NxActionsDrawer({
  agentId,
  label,
  elevatedCredentialId,
  loginPower,
  onClose,
  onChanged,
  logins,
  onChangeLogin,
}: {
  agentId: string;
  label: string;
  elevatedCredentialId: string | null;
  loginPower: boolean | null;
  onClose: () => void;
  onChanged: () => void;
  logins?: HostLogins | undefined;
  onChangeLogin?: (() => void) | undefined;
}): React.JSX.Element {
  const [credentials, setCredentials] = useState<CredentialOption[]>([]);
  const [elevated, setElevated] = useState<string>(elevatedCredentialId ?? '');
  const [why, setWhy] = useState('');
  const [rows, setRows] = useState<ActionRow[]>([]);
  const [shown, setShown] = useState<{ id: string; text: string } | null>(null);
  const [waiting, setWaiting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ credentials: CredentialOption[] }>('/api/credentials')
      .then((r) => setCredentials(r.credentials.filter((c) => c.kind === 'userpass')))
      .catch((err: unknown) => setError(failure(err)));
  }, []);

  const list = useCallback(async () => {
    const res = await api.get<{ logs: ActionRow[] }>(`/api/agents/${agentId}/logs`);
    const mine = res.logs.filter((l) => l.reason === 'nx_action');
    setRows(mine);
    return mine;
  }, [agentId]);

  const show = useCallback(async (id: string) => {
    const got = await api.get<{ content: string | null }>(`/api/agent-logs/${id}`);
    setShown({ id, text: got.content ?? '' });
  }, []);

  useEffect(() => {
    void list().catch((err: unknown) => setError(failure(err)));
  }, [list]);

  useEffect(() => {
    if (waiting === null) return;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const row = (await list()).find((r) => r.id === waiting);
          if (row?.receivedAt != null) {
            setWaiting(null);
            await show(row.id);
          }
        } catch (err) {
          setError(failure(err));
        }
      })();
    }, 3000);
    return () => clearInterval(timer);
  }, [waiting, list, show]);

  const saveElevated = async (value: string): Promise<void> => {
    setError(null);
    setElevated(value);
    try {
      await api.put(`/api/agents/${agentId}/nx-elevated`, { credentialId: value === '' ? null : value });
      onChanged();
    } catch (err) {
      setError(failure(err));
    }
  };

  const run = async (action: string): Promise<void> => {
    setError(null);
    try {
      const res = await api.post<{ id: string }>(`/api/agents/${agentId}/nx-action`, { action, why });
      setWaiting(res.id);
      setShown(null);
      await list();
    } catch (err) {
      setError(failure(err));
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={`NX actions — ${label}`}
      subtitle="Read-only checks that need more than the monitoring login. Each is audited with your reason."
      width="lg"
    >
      <div className="stack-tight">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {loginPower === true && (
          <Banner tone="warn" title="The monitoring login can change NX">
            It reached a Power-User-only page. It is held on the host all the time, so give it view-only rights (Site Health
            Viewers + Viewers) and choose a separate Power User login below for these actions.
          </Banner>
        )}
        <Field
          id="nx-elevated"
          label="Elevated login (Power User)"
          hint="Handed to the agent for one action, within ten minutes of asking, then forgotten. None: actions use the monitoring login."
        >
          <select id="nx-elevated" value={elevated} onChange={(e) => void saveElevated(e.target.value)}>
            <option value="">None — use the monitoring login</option>
            {credentials.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </Field>
        <Field id="nx-why" label="Why" hint="Kept with the request and in Activity.">
          <input id="nx-why" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="e.g. C07 offline since this morning" />
        </Field>
        {ACTIONS.map((a) => (
          <div key={a.key} className="row-tight">
            <Button size="sm" icon={<Play size={14} />} disabled={why.trim().length < 5 || waiting !== null} onClick={() => void run(a.key)}>
              {a.label}
            </Button>
            <span className="small muted">{a.what}</span>
          </div>
        ))}
        {waiting !== null && (
          <Banner tone="info" title="Asked the agent">
            It answers within seconds when it is connected; the request lapses after ten minutes.
          </Banner>
        )}
        {rows.length > 0 && (
          <ul className="agent-log-list small">
            {rows.map((r) => (
              <li key={r.id}>
                <button type="button" className="agent-log-pick" disabled={r.receivedAt === null} onClick={() => void show(r.id)}>
                  {new Date(r.requestedAt).toLocaleString()}
                </button>{' '}
                · {ACTIONS.find((a) => a.key === r.action)?.label ?? r.action} · {r.why}
                {r.receivedAt === null ? ' · waiting' : ''}
                {shown?.id === r.id && ' · shown'}
              </li>
            ))}
          </ul>
        )}
        {shown !== null && <NxResultView raw={shown.text} label={label} logins={logins} onChangeLogin={onChangeLogin} />}
      </div>
    </Drawer>
  );
}
